import React, { useState, useEffect } from 'react';
import { Users, Wifi, Car, Coffee, Tv, Wind, Bath, BedDouble } from 'lucide-react';
import { getEndpointUrl } from '../config/api';
import { toast } from './Toast';
import BookRooms from './BookRoomsSimple';
import './AllRooms.css';

const formatDateForApi = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getRoomSortPriority = (room) => {
  const roomText = `${room?.name || ''} ${room?.type || ''}`.toLowerCase();
  if (roomText.includes('super') && (roomText.includes('delux') || roomText.includes('deluxe'))) {
    return 1;
  }
  if (roomText.includes('delux') || roomText.includes('deluxe')) {
    return 0;
  }
  if (roomText.includes('premier') || roomText.includes('premium')) {
    return 2;
  }
  if (roomText.includes('executive')) {
    return 3;
  }
  return 99;
};

const sortRoomsByDisplayOrder = (roomsList) => {
  return [...roomsList].sort((left, right) => {
    const leftPriority = getRoomSortPriority(left);
    const rightPriority = getRoomSortPriority(right);

    if (leftPriority !== rightPriority) {
      return leftPriority - rightPriority;
    }

    return String(left?.name || '').localeCompare(String(right?.name || ''));
  });
};

const AllRooms = ({ user }) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isBookRoomsOpen, setIsBookRoomsOpen] = useState(false);

  // Fetch all room types from backend (show all categories, even if fully booked)
  useEffect(() => {
    const fetchAllRooms = async () => {
      try {
        setLoading(true);
        const cacheBust = Date.now();
        const url = `${getEndpointUrl('ROOMS', 'ALL')}?refresh=${cacheBust}`;
        const today = formatDateForApi(new Date());
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = formatDateForApi(tomorrow);
        
        const response = await fetch(url, { cache: 'no-store' });
        
        if (response.ok) {
          const roomsData = await response.json();
          const sortedRooms = sortRoomsByDisplayOrder(Array.isArray(roomsData) ? roomsData : []);
          const roomsWithTodayPricing = await Promise.all(sortedRooms.map(async (room) => {
            try {
              const pricingResponse = await fetch(
                `/api/public/pricing/check?roomId=${room.id}&checkIn=${today}&checkOut=${tomorrowStr}&refresh=${cacheBust}`,
                { cache: 'no-store' }
              );

              if (!pricingResponse.ok) {
                return room;
              }

              const roomPricing = await pricingResponse.json();
              if (roomPricing?.customPricing) {
                return {
                  ...room,
                  singleOccupancyPrice: roomPricing.singleOccupancyPrice,
                  doubleOccupancyPrice: roomPricing.doubleOccupancyPrice,
                  currentPricingNote: roomPricing.notes || 'Today\'s rate',
                  isDynamicPricing: true
                };
              }

              return room;
            } catch (pricingError) {
              console.error(`Error loading current pricing for room ${room.id}:`, pricingError);
              return room;
            }
          }));

          setRooms(roomsWithTodayPricing);
        } else {
          console.error('Failed to fetch rooms');
          toast.error('Failed to load rooms');
          setRooms([]);
        }
      } catch (error) {
        console.error('Error fetching rooms:', error);
        toast.error('Error loading rooms');
        setRooms([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllRooms();

    // Refresh when user returns to this tab so admin pricing updates appear immediately.
    const handleWindowFocus = () => {
      fetchAllRooms();
    };
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  const getFacilityIcon = (facility) => {
    const iconMap = {
      'WiFi': Wifi,
      'AC': Wind,
      'TV': Tv,
      'Mini Bar': Coffee,
      'Room Service': Coffee,
      'Balcony': Bath,
      'Work Desk': Bath,
      'Coffee Maker': Coffee,
      'Parking': Car
    };
    const IconComponent = iconMap[facility] || Coffee;
    return <IconComponent size={16} />;
  };

  const handleBookRoom = (room) => {
    console.log('Room selected for booking:', room);
    // Don't set selectedRoom to null to allow all room types to be shown
    // Just open the booking modal with all available rooms
    setIsBookRoomsOpen(true);
  };

  const handleBookRoomsClose = () => {
    setIsBookRoomsOpen(false);
  };

  const getRoomImage = (room) => {
    // Use same logic as other components for room images
    const fallbackSVG = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjZjNmNGY2Ii8+CjxyZWN0IHg9IjUwIiB5PSI1MCIgd2lkdGg9IjIwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNlNWU3ZWIiLz4KPHN2ZyB4PSIxMjAiIHk9Ijg1IiB3aWR0aD0iNjAiIGhlaWdodD0iMzAiIHZpZXdCb3g9IjAgMCA2MCAzMCIgZmlsbD0ibm9uZSI+CjxyZWN0IHdpZHRoPSI2MCIgaGVpZ2h0PSIzMCIgZmlsbD0iIzllYTNhZiIvPgo8dGV4dCB4PSIzMCIgeT0iMjAiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iI2ZmZmZmZiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+Um9vbTwvdGV4dD4KPC9zdmc+Cjwvc3ZnPgo=';
    
    if (room.imageUrl && !room.imageUrl.includes('/api/placeholder/')) {
      return room.imageUrl;
    }
    if (room.image && !room.image.includes('/api/placeholder/')) {
      return room.image;
    }
    return fallbackSVG;
  };

  if (loading) {
    return (
      <div className="all-rooms-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading rooms...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="all-rooms-container">
      <div className="all-rooms-header">
        <h1>Our Rooms</h1>
        <p>Discover comfort and luxury in our carefully designed accommodations</p>
      </div>

      {rooms.length === 0 ? (
        <div className="no-rooms-message">
          <h3>No rooms available at the moment</h3>
          <p>Please check back later or contact us for assistance.</p>
        </div>
      ) : (
        <div className="rooms-grid">
          {rooms.map((room) => (
            <div key={room.id} className="room-card">
              {/* Room Image */}
              <div className="room-image-container">
                <img 
                  src={getRoomImage(room)}
                  alt={room.name}
                  className="room-image"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjZjNmNGY2Ii8+CjxyZWN0IHg9IjUwIiB5PSI1MCIgd2lkdGg9IjIwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNlNWU3ZWIiLz4KPHN2ZyB4PSIxMjAiIHk9Ijg1IiB3aWR0aD0iNjAiIGhlaWdodD0iMzAiIHZpZXdCb3g9IjAgMCA2MCAzMCIgZmlsbD0ibm9uZSI+CjxyZWN0IHdpZHRoPSI2MCIgaGVpZ2h0PSIzMCIgZmlsbD0iIzllYTNhZiIvPgo8dGV4dCB4PSIzMCIgeT0iMjAiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iI2ZmZmZmZiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+Um9vbTwvdGV4dD4KPC9zdmc+Cjwvc3ZnPgo=';
                  }}
                />
                {/* <div className="room-availability-badge">
                  <span className={`availability-status ${room.availableRooms > 0 ? 'available' : 'unavailable'}`}>
                    {room.availableRooms > 0 ? `${room.availableRooms} Available Now` : 'Select Dates To Check Availability'}
                  </span>
                </div> */}
              </div>

              {/* Room Details */}
              <div className="room-content">
                <div className="room-header">
                  <h3 className="room-name">{room.name}</h3>
                  <div className="room-type-badge">
                    <span className={`type-badge ${room.type?.toLowerCase()}`}>
                      {room.type}
                    </span>
                  </div>
                </div>

                <div className="room-info">
                  <div className="room-capacity">
                    <Users size={16} />
                    <span>{room.maxOccupancy} Guests</span>
                  </div>
                  <div className="room-size">
                    <BedDouble size={16} />
                    <span>{room.size}</span>
                  </div>
                </div>

                <div className="complimentary-breakfast-badge">
                  Complimentary breakfast included
                </div>

                {/* Room Description */}
                {room.description && (
                  <p className="room-description">{room.description}</p>
                )}

                {/* Facilities */}
                {room.facilities && room.facilities.length > 0 && (
                  <div className="room-facilities">
                    <h4>Amenities</h4>
                    <div className="facilities-list">
                      {room.facilities.map((facility, index) => (
                        <div key={index} className="facility-item">
                          {getFacilityIcon(facility)}
                          <span>{facility}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pricing */}
                <div className="room-pricing">
                  <div className="price-section">
                    <div className="price-item">
                      <span className="price-label">Single Occupancy</span>
                      <span className="price-value">₹{room.singleOccupancyPrice}</span>
                    </div>
                    <div className="price-item">
                      <span className="price-label">Double Occupancy</span>
                      <span className="price-value">₹{room.doubleOccupancyPrice}</span>
                    </div>
                  </div>
                  <div className="price-note">
                    {room.currentPricingNote || 'per night'}
                  </div>
                </div>

                {/* Availability Summary */}
                {/* <div className="room-availability-summary">
                  <div className="availability-info">
                    <span className="available-count">{room.availableRooms}/{room.totalRooms}</span>
                    <span className="availability-text">currently available (final availability depends on selected dates)</span>
                  </div>
                </div> */}

                {/* Book Button */}
                <button 
                  className="book-room-btn"
                  onClick={() => handleBookRoom(room)}
                >
                  <>
                    <span className="btn-label-desktop">Check Dates & Book</span>
                    <span className="btn-label-mobile">Check Dates</span>
                  </>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Book Rooms Modal */}
      {isBookRoomsOpen && (
        <BookRooms 
          onClose={handleBookRoomsClose}
          user={user}
          selectedRoomFilter={null}
        />
      )}
    </div>
  );
};

export default AllRooms;
